<template>
  <div class="profit-chart-wrapper">
    <div class="chart-legend">
      <div class="legend-item">
        <div class="legend-line"></div>
        <div class="legend-text">{{ $t('accountAnalysis.a00007') }}</div>
      </div>
    </div>
    <div class="chart-container">
      <div ref="chartDom" class="chart-dom"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, GridComponent, DataZoomComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

echarts.use([TitleComponent, TooltipComponent, GridComponent, DataZoomComponent, LineChart, CanvasRenderer])

export default {
  name: 'ProfitChart',
  props: {
    chartData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chartInstance: null,
      resizeTimer: null,
      // 图表颜色配置
      chartColor: '#2645BC',
      chartColorRgba: 'rgba(38,69,188,0.3)',
      chartColorTransparent: 'rgba(38,69,188,0)'
    }
  },
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.renderChart()
        })
      },
      deep: true
    }
  },
  mounted() {
    this.initChart()
    this.renderChart()
    this.startAutoResize()
  },
  beforeDestroy() {
    this.stopAutoResize()
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    // 初始化图表实例
    initChart() {
      if (this.$refs.chartDom) {
        this.chartInstance = echarts.init(this.$refs.chartDom)
      }
    },

    // 渲染图表
    renderChart() {
      if (!this.chartInstance) return

      const { dateList, netValueList } = this.processChartData()
      const options = this.getChartOptions(dateList, netValueList)
      
      this.chartInstance.setOption(options)
      this.chartInstance.dispatchAction({ type: 'hideTip' })
    },

    // 处理图表数据
    processChartData() {
      if (!this.chartData || this.chartData.length === 0) {
        return { dateList: [], netValueList: [] }
      }

      const dateList = []
      const netValueList = []

      this.chartData.forEach(item => {
        dateList.push(item.date)
        netValueList.push(item.total_net_value ? item.total_net_value.toFixed(3) : 0)
      })

      return {
        dateList: dateList.reverse(),
        netValueList: netValueList.reverse()
      }
    },

    // 获取图表配置
    getChartOptions(dateList, netValueList) {
      return {
        tooltip: this.getTooltipConfig(),
        grid: this.getGridConfig(),
        xAxis: this.getXAxisConfig(dateList),
        yAxis: this.getYAxisConfig(),
        dataZoom: this.getDataZoomConfig(),
        series: this.getSeriesConfig(netValueList)
      }
    },

    // Tooltip配置
    getTooltipConfig() {
      return {
        trigger: 'axis',
        backgroundColor: 'rgba(0,0,0,0.5)',
        textStyle: {
          color: '#ffffff'
        },
        borderWidth: 0,
        formatter: (params) => {
          if (!params || !params[0]) return ''
          return `<span>${params[0].axisValue}</span></br><span style="color:${this.chartColor};font-size:18px">●</span>&nbsp;<span style='font-size:15px;color:#fff'>${this.$t('accountAnalysis.a00007')}</span>:<span style='margin-left:20px;font-size:16px;font-weight:600'>${params[0].data}</span>`
        },
        position: this.tooltipPosition
      }
    },

    // Tooltip位置计算（防止越界）
    tooltipPosition(point, params, dom, rect, size) {
      const x = point[0]
      const y = point[1]
      const boxWidth = size.contentSize[0]
      const boxHeight = size.contentSize[1]
      
      let posX = 0
      let posY = 0

      if (x < boxWidth) {
        posX = 10
      } else {
        posX = x - boxWidth
      }

      if (y < boxHeight) {
        posY = 1
      } else {
        posY = y - boxHeight
      }

      return [posX, posY]
    },

    // Grid配置
    getGridConfig() {
      return {
        left: '10%',
        right: '9%',
        top: 12,
        bottom: 30
      }
    },

    // X轴配置
    getXAxisConfig(data) {
      return {
        type: 'category',
        data: data,
        axisLabel: {
          show: true,
          color: '#888888',
          showMaxLabel: true
        },
        axisLine: {
          lineStyle: {
            color: '#EEEEEE'
          }
        },
        axisTick: {
          show: false
        }
      }
    },

    // Y轴配置
    getYAxisConfig() {
      return {
        type: 'value',
        axisLabel: {
          margin: -30,
          inside: true,
          color: '#888888',
          formatter: this.yAxisFormatter
        },
        scale: true,
        splitNumber: 4,
        splitLine: {
          lineStyle: {
            color: '#EEEEEE'
          }
        }
      }
    },

    // Y轴格式化
    yAxisFormatter(value) {
      return value.toFixed(2) + '\n'
    },

    // DataZoom配置
    getDataZoomConfig() {
      return [
        {
          type: 'inside',
          start: 0,
          end: 100,
          preventDefaultMouseMove: false
        },
        {
          show: false,
          type: 'slider',
          start: 0,
          end: 100,
          preventDefaultMouseMove: false
        }
      ]
    },

    // Series配置
    getSeriesConfig(data) {
      return [
        {
          name: this.$t('accountAnalysis.a00007'),
          type: 'line',
          data: data,
          smooth: true,
          symbol: data.length > 1 ? 'none' : false,
          itemStyle: {
            normal: this.chartColor
          },
          lineStyle: {
            normal: {
              color: this.chartColor,
              width: 1
            }
          },
          areaStyle: {
            normal: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: this.chartColorRgba
                },
                {
                  offset: 1,
                  color: this.chartColorTransparent
                }
              ])
            }
          }
        }
      ]
    },

    // 开始自动调整大小
    startAutoResize() {
      this.resizeTimer = setInterval(() => {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      }, 50)
    },

    // 停止自动调整大小
    stopAutoResize() {
      if (this.resizeTimer) {
        clearInterval(this.resizeTimer)
        this.resizeTimer = null
      }
    }
  }
}
</script>

<style lang="less" scoped>
.profit-chart-wrapper {
  padding: 0px 20px;
  background: white;
  border-radius: 24px;
}

.chart-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 40px;
  line-height: 40px;
  padding-top: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
}

.legend-line {
  width: 26px;
  height: 4px;
  background-color: #2645BC;
}

.legend-text {
  font-size: 20px;
  color: #888888;
  padding-left: 10px;
}

.chart-container {
  width: 100%;
}

.chart-dom {
  width: 100%;
  height: 400px;
}
</style>

